import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../App';
import axios from 'axios';

const AnuncioDashboardWidget = () => {
  const { user, API } = useContext(AuthContext);
  const [anuncio, setAnuncio] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAnuncio();
  }, []);

  const fetchAnuncio = async () => {
    try {
      const res = await axios.get(`${API}/public/anuncio-dashboard`);
      if (res.data?.activo) {
        setAnuncio(res.data);
      }
    } catch {
      // silently fail
    } finally {
      setLoading(false);
    }
  };

  if (loading || !anuncio) return null;

  const audiencia = anuncio.audiencia || 'todos';
  if (audiencia === 'estudiantes_pastores' && !['estudiante', 'pastor'].includes(user?.rol)) return null;
  if (audiencia === 'profesores' && user?.rol !== 'profesor') return null;

  const bgColor = anuncio.fondo_color || '#f0f9ff';
  const borderColor = anuncio.borde_color || '#3b82f6';
  const titleColor = anuncio.titulo_color || '#1e3a8a';
  const descColor = anuncio.descripcion_color || '#374151';
  const hasMedia = anuncio.tipo_media && anuncio.tipo_media !== 'ninguno' && anuncio.media_url;
  const isImage = anuncio.tipo_media === 'imagen';
  const isVideo = anuncio.tipo_media === 'video';
  const isAudio = anuncio.tipo_media === 'audio';

  const getVideoEmbed = (url) => {
    const ytMatch = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&?/]+)/);
    if (ytMatch) return `https://www.youtube.com/embed/${ytMatch[1]}`;
    const vimeoMatch = url.match(/vimeo\.com\/(\d+)/);
    if (vimeoMatch) return `https://player.vimeo.com/video/${vimeoMatch[1]}`;
    return url;
  };

  return (
    <div
      className={`group relative rounded-2xl p-5 cursor-default transform transition-all duration-300 hover:scale-105 hover:shadow-2xl overflow-hidden ${hasMedia && isImage ? 'row-span-2 h-full' : 'h-56'}`}
      style={{ backgroundColor: bgColor, borderLeft: `4px solid ${borderColor}` }}
      data-testid="anuncio-dashboard-widget"
    >
      {/* Shine effect */}
      <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/15 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000" />

      <div className="relative z-10 h-full flex flex-col justify-between">
        {/* Top: icon + title */}
        <div>
          <div className="flex items-center gap-2 mb-2">
            <div className="p-1.5 rounded-full" style={{ backgroundColor: borderColor + '33' }}>
              <i className="fas fa-bullhorn text-lg" style={{ color: titleColor }} />
            </div>
            {anuncio.titulo && (
              <h3
                className="text-sm font-bold leading-tight line-clamp-2"
                style={{ color: titleColor }}
                data-testid="anuncio-dashboard-titulo"
              >
                {anuncio.titulo}
              </h3>
            )}
          </div>

          {anuncio.descripcion && (
            <p
              className={`text-xs leading-snug ${hasMedia ? 'line-clamp-2' : 'line-clamp-6 whitespace-pre-wrap'}`}
              style={{ color: descColor }}
              data-testid="anuncio-dashboard-descripcion"
            >
              {anuncio.descripcion}
            </p>
          )}
        </div>

        {/* Middle: media */}
        {isImage && (
          <div className="flex-1 flex items-center justify-center py-2 min-h-0">
            <img
              src={anuncio.media_url}
              alt={anuncio.titulo || 'Anuncio'}
              className="max-h-full max-w-full object-contain rounded-lg"
              data-testid="anuncio-dashboard-imagen"
            />
          </div>
        )}

        {isVideo && (
          <div className="flex-1 min-h-0 py-2">
            <div className="rounded-lg overflow-hidden h-full" data-testid="anuncio-dashboard-video">
              <iframe
                src={getVideoEmbed(anuncio.media_url)}
                className="w-full h-full"
                allow="autoplay; encrypted-media"
                allowFullScreen
                title="Anuncio video"
              />
            </div>
          </div>
        )}

        {isAudio && (
          <div className="py-2" data-testid="anuncio-dashboard-audio">
            <audio controls className="w-full h-8" src={anuncio.media_url}>
              Tu navegador no soporta audio.
            </audio>
          </div>
        )}

        {/* If no media, add spacing */}
        {!hasMedia && <div className="flex-1" />}

        {/* Bottom: action button */}
        {anuncio.boton_activo && anuncio.boton_texto && (
          <a
            href={anuncio.boton_link || '#'}
            target={anuncio.boton_link?.startsWith('http') ? '_blank' : '_self'}
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center gap-1.5 px-4 py-1.5 rounded-full text-xs font-semibold transition-all duration-200 hover:opacity-90 w-full text-center"
            style={{ backgroundColor: anuncio.boton_color || borderColor, color: '#ffffff' }}
            data-testid="anuncio-dashboard-boton"
          >
            {anuncio.boton_texto}
            <i className="fas fa-arrow-right text-[10px]" />
          </a>
        )}
      </div>

      {/* Decorative circles */}
      <div className="absolute top-0 right-0 w-20 h-20 rounded-full -mr-8 -mt-8 opacity-10" style={{ backgroundColor: borderColor }} />
      <div className="absolute bottom-0 left-1/2 w-14 h-14 rounded-full -mb-6 opacity-5" style={{ backgroundColor: borderColor }} />
    </div>
  );
};

export default AnuncioDashboardWidget;
